/**
 * 统一请求封装.
 *
 * - 只用相对路径, 开发期由 vite 代理转发到 FastAPI, 生产与页面同域;
 * - 非 2xx 响应解析后端统一错误信封 { error: { code, message, details } };
 * - 令牌存 localStorage, 由 stores/auth.ts 在登录/登出时维护.
 */

import type {
  AuthResponse,
  Course,
  CourseDetail,
  CourseTags,
  CourseProgress,
  ErrorEnvelope,
  FavoriteCourse,
  FavoriteSentence,
  FavoriteTarget,
  FavoriteWord,
  HighlightOptions,
  InviteSummary,
  MembershipRecord,
  Page,
  PageParams,
  RecentCourseProgress,
  SentenceHighlight,
  User,
  WordCard,
  WordLookupItem,
} from '../types/api'

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '') || '/api/v1'
const TOKEN_KEY = 'oma-gendu:token'

/** 请求层通知应用清理已失效的登录态, 避免反向依赖认证 store. */
export const AUTH_EXPIRED_EVENT = 'auth-session-expired'

/** 仅注销发起请求时的会话, 避免旧响应清掉新登录的令牌. */
function handleAuthFailure(path: string, token: string | null, status: number, payload: unknown): void {
  if (path === '/auth/login' || path === '/auth/register') return
  const disabled = status === 403 && isErrorEnvelope(payload) && payload.error.code === 'account_disabled'
  if ((status === 401 || disabled) && token === getToken()) {
    setToken(null)
    window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT))
  }
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: unknown

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

export function setToken(token: string | null): void {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token)
  } else {
    localStorage.removeItem(TOKEN_KEY)
  }
}

function isErrorEnvelope(value: unknown): value is ErrorEnvelope {
  return (
    typeof value === 'object' &&
    value !== null &&
    'error' in value &&
    typeof (value as ErrorEnvelope).error?.message === 'string'
  )
}

function buildQuery(params: PageParams = {}): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value))
  }
  const query = search.toString()
  return query ? `?${query}` : ''
}

export async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken()
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(init.body ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }

  const response = await fetch(`${BASE_URL}${path}`, { ...init, headers })

  if (response.status === 204) {
    return undefined as T
  }

  const payload: unknown = await response.json().catch(() => null)

  if (!response.ok) {
    handleAuthFailure(path, token, response.status, payload)
    if (isErrorEnvelope(payload)) {
      const { code, message, details } = payload.error
      throw new ApiError(response.status, code, message, details)
    }
    throw new ApiError(response.status, `http_${response.status}`, response.statusText)
  }

  return payload as T
}

export const api = {
  auth: {
    /** 注册需推荐人的邀请码(不区分大小写)与自设密码 */
    register: (email: string, inviteCode: string, password: string) =>
      request<AuthResponse>('/auth/register', {
        method: 'POST',
        body: JSON.stringify({ email, invite_code: inviteCode, password }),
      }),
    login: (email: string, password: string) =>
      request<AuthResponse>('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      }),
    me: () => request<User>('/auth/me'),
  },
  users: {
    /** 我的邀请码与邀请记录(邀请码本身也在 user.invite_code 里) */
    invite: () => request<InviteSummary>('/users/me/invite'),
    /** 改昵称与内容语言; 只提交需要变更的字段 */
    updateProfile: (payload: { nickname?: string; language?: string }) =>
      request<User>('/users/me', { method: 'PATCH', body: JSON.stringify(payload) }),
    changePassword: (currentPassword: string, newPassword: string) =>
      request<void>('/users/me/password', {
        method: 'POST',
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      }),
  },
  membership: {
    /** 只读本人历史, 会员到期后仍可查看. */
    history: (params?: PageParams) =>
      request<Page<MembershipRecord>>(`/membership/history${buildQuery(params)}`),
    redeem: (code: string) =>
      request<User>('/membership/redeem', { method: 'POST', body: JSON.stringify({ code }) }),
  },
  courses: {
    list: (params?: PageParams) => request<Page<Course>>(`/courses${buildQuery(params)}`),
    /** 热门资源: 按打开人数(UV)倒序, 首页板块用 */
    popular: (limit = 10) => request<Course[]>(`/courses/popular?limit=${limit}`),
    /** 筛选条: 已上架资源总数与各标签资源数, 与分页无关 */
    tags: () => request<CourseTags>('/courses/tags'),
    detail: (id: number | string) => request<CourseDetail>(`/courses/${id}`),
    /** 按当前用户的高亮词表返回整份资源的高亮区间 */
    highlights: (id: number | string) =>
      request<SentenceHighlight[]>(`/courses/${id}/highlights`),
  },
  favorites: {
    /** 已收藏的 target id 列表, 跟读页用它标记图标状态 */
    savedIds: (targetType: FavoriteTarget = 'sentence') =>
      request<number[]>(`/favorites/ids${buildQuery({ target_type: targetType })}`),
    sentences: (params?: PageParams) =>
      request<Page<FavoriteSentence>>(`/favorites/sentences${buildQuery(params)}`),
    courses: (params?: PageParams) =>
      request<Page<FavoriteCourse>>(`/favorites/courses${buildQuery(params)}`),
    words: (params?: PageParams) =>
      request<Page<FavoriteWord>>(`/favorites/words${buildQuery(params)}`),
    add: (targetType: FavoriteTarget, targetId: number) =>
      request<{ target_type: string; target_id: number; course_id: number }>('/favorites', {
        method: 'POST',
        body: JSON.stringify({ target_type: targetType, target_id: targetId }),
      }),
    remove: (targetType: FavoriteTarget, targetId: number) =>
      request<void>(`/favorites/${targetType}/${targetId}`, { method: 'DELETE' }),
  },
  highlight: {
    /** 高亮配置: 三个等级的可选词表 + 当前用户已选 */
    get: () => request<HighlightOptions>('/highlight'),
    /** 整表替换已选词表 */
    save: (tags: string[]) =>
      request<string[]>('/highlight', { method: 'PUT', body: JSON.stringify({ tags }) }),
  },
  words: {
    /** 按词头前缀或中文释义分页查词. */
    search: (q: string, limit = 20, offset = 0) =>
      request<Page<WordLookupItem>>(`/words${buildQuery({ q, limit, offset })}`),
    /** 单词卡片(点高亮词时按 word_id 拉取) */
    get: (id: number) => request<WordCard>(`/words/${id}`),
  },
  progress: {
    /** 读取某资源的跟读进度; 没有记录时后端返回 null */
    get: (courseId: number | string) => request<CourseProgress | null>(`/progress/${courseId}`),
    /** 最近看过的资源(首页续播卡与「更多资源」按它排序) */
    recent: (limit?: number) =>
      request<RecentCourseProgress[]>(`/progress/recent${buildQuery({ limit })}`),
    /** 上报跟读进度(整条覆盖, 多端最后一次写入为准) */
    save: (courseId: number | string, positionSeconds: number, sentenceId: number) =>
      request<CourseProgress>(`/progress/${courseId}`, {
        method: 'PUT',
        body: JSON.stringify({
          position_seconds: positionSeconds,
          sentence_id: sentenceId,
        }),
      }),
  },
}
